import React, { forwardRef } from "react";
import { LogoLoader } from "./Loader";

/**
 * Button — Ocean Blue Design System
 *
 * Props:
 *  variant   : "primary" | "secondary" | "ghost" | "success" | "warning" | "danger"
 *  size      : "sm" | "md" | "lg" | "xl"
 *  isLoading : boolean
 *  isFullWidth: boolean
 *  leftIcon  : ReactNode
 *  rightIcon : ReactNode
 *  iconOnly  : boolean   — square icon button
 */

const VARIANTS = {
  primary:
    "bg-[#004BB5] text-white border-[#004BB5] " +
    "focus:ring-[#004BB5]/30 active:bg-[#00337E]",
  secondary:
    "bg-white text-ink-700 border-border " +
    "focus:ring-[#004BB5]/20 active:bg-canvas",
  ghost:
    "bg-transparent text-ink-500 border-transparent " +
    "focus:ring-ink-300/30 active:bg-canvas",
  success:
    "bg-success-500 text-white border-success-500 " +
    "focus:ring-success-500/30 active:bg-success-600",
  warning:
    "bg-warning-500 text-white border-warning-500 " +
    "focus:ring-warning-500/30 active:bg-warning-600",
  danger:
    "bg-danger-500 text-white border-danger-500 " +
    "focus:ring-danger-500/30 active:bg-danger-600",
  "danger-ghost":
    "bg-transparent text-danger-500 border-transparent " +
    "focus:ring-danger-500/20",
};

const SIZES = {
  sm: "px-3 py-1.5 text-xs rounded-md gap-1.5",
  md: "px-4 py-2 text-sm rounded-lg gap-2",
  lg: "px-5 py-2.5 text-sm rounded-lg gap-2",
  xl: "px-7 py-3.5 text-base rounded-xl gap-2.5 font-semibold",
};

const ICON_SIZES = {
  sm: "p-1.5 rounded-md",
  md: "p-2 rounded-lg",
  lg: "p-2.5 rounded-lg",
  xl: "p-3 rounded-xl",
};

const BASE =
  "inline-flex items-center justify-center font-sans font-medium border " +
  "select-none cursor-pointer focus:outline-none focus:ring-2 focus:ring-offset-1 " +
  "disabled:opacity-50 disabled:cursor-not-allowed disabled:pointer-events-none " +
  "disabled:shadow-none";

const Button = forwardRef(
  (
    {
      children,
      variant = "primary",
      size = "md",
      isLoading = false,
      isFullWidth = false,
      leftIcon,
      rightIcon,
      iconOnly = false,
      className = "",
      type = "button",
      ...props
    },
    ref
  ) => {
    const variantClass = VARIANTS[variant] ?? VARIANTS.primary;
    const sizeClass = iconOnly ? ICON_SIZES[size] : SIZES[size];

    return (
      <button
        ref={ref}
        type={type}
        className={[
          BASE,
          variantClass,
          sizeClass,
          isFullWidth ? "w-full" : "",
          isLoading ? "relative text-transparent pointer-events-none" : "",
          className,
        ]
          .filter(Boolean)
          .join(" ")}
        disabled={isLoading || props.disabled}
        {...props}
      >
        {/* Loading spinner overlay */}
        {isLoading && (
          <span className="absolute inset-0 flex items-center justify-center">
            <LogoLoader size="xs" className="p-0" />
          </span>
        )}

        {leftIcon && !isLoading && (
          <span className="flex-shrink-0 inline-flex">{leftIcon}</span>
        )}
        {!iconOnly && children}
        {rightIcon && !isLoading && (
          <span className="flex-shrink-0 inline-flex">{rightIcon}</span>
        )}
        {iconOnly && !isLoading && children}
      </button>
    );
  }
);

Button.displayName = "Button";
export default Button;
